import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getLogs, clearOldLogs } from '../../api/client';

// ── Constants ──────────────────────────────────────────────────────────────

const LEVELS = ['', 'WARNING', 'ERROR', 'CRITICAL'];
const TAGS   = ['', 'STRATEGY', 'ENGINE', 'RISK', 'FYERS', 'SYSTEM'];

const LEVEL_STYLE = {
  WARNING:  { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)',  border: 'rgba(245,158,11,0.3)'  },
  ERROR:    { color: '#ef4444', bg: 'rgba(239,68,68,0.12)',   border: 'rgba(239,68,68,0.3)'   },
  CRITICAL: { color: '#dc2626', bg: 'rgba(220,38,38,0.18)',   border: 'rgba(220,38,38,0.4)'   },
};

const TAG_STYLE = {
  STRATEGY: { color: 'var(--accent)',  bg: 'rgba(99,102,241,0.12)',  border: 'rgba(99,102,241,0.3)'  },
  ENGINE:   { color: '#a78bfa',        bg: 'rgba(167,139,250,0.12)', border: 'rgba(167,139,250,0.3)' },
  RISK:     { color: '#f97316',        bg: 'rgba(249,115,22,0.12)',  border: 'rgba(249,115,22,0.3)'  },
  FYERS:    { color: 'var(--green)',   bg: 'rgba(34,197,94,0.10)',   border: 'rgba(34,197,94,0.25)'  },
  SYSTEM:   { color: 'var(--muted)',   bg: 'rgba(148,163,184,0.10)', border: 'rgba(148,163,184,0.2)' },
};

function Badge({ text, styleMap }) {
  const s = styleMap[text] || { color: 'var(--muted)', bg: 'transparent', border: 'var(--border)' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 99,
      fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
      color: s.color, background: s.bg, border: `1px solid ${s.border}`,
      whiteSpace: 'nowrap',
    }}>
      {text}
    </span>
  );
}

function FilterSelect({ label, value, options, onChange }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 110 }}>
      <label style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </label>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
          background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)',
          borderRadius: 6, padding: '5px 8px', fontSize: 12, cursor: 'pointer',
        }}
      >
        {options.map(o => (
          <option key={o} value={o}>{o || 'All'}</option>
        ))}
      </select>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export default function AppLogs() {
  const [logs,        setLogs]        = useState([]);
  const [total,       setTotal]       = useState(0);
  const [pages,       setPages]       = useState(1);
  const [page,        setPage]        = useState(1);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');
  const [expanded,    setExpanded]    = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [clearing,    setClearing]    = useState(false);
  const [clearMsg,    setClearMsg]    = useState('');

  const [filters, setFilters] = useState({
    level:     '',
    tag:       '',
    search:    '',
    date_from: '',
    date_to:   '',
  });

  const searchTimeout = useRef(null);

  const fetchLogs = useCallback(async (pg = page, f = filters) => {
    setLoading(true);
    setError('');
    try {
      const res = await getLogs({ ...f, page: pg, limit: 50 });
      setLogs(res.data.logs);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch {
      setError('Failed to load logs.');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  // Fetch on page/filter change
  useEffect(() => { fetchLogs(page, filters); }, [page]);   // eslint-disable-line

  // Auto-refresh every 10 s
  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(() => fetchLogs(page, filters), 10_000);
    return () => clearInterval(t);
  }, [autoRefresh, page, filters, fetchLogs]);

  // Apply filter changes — reset to page 1, debounce search input
  const applyFilter = (key, value) => {
    const updated = { ...filters, [key]: value };
    setFilters(updated);
    setPage(1);
    if (key === 'search') {
      clearTimeout(searchTimeout.current);
      searchTimeout.current = setTimeout(() => fetchLogs(1, updated), 400);
    } else {
      fetchLogs(1, updated);
    }
  };

  const handleClear = async () => {
    if (!window.confirm('Delete all log entries older than 7 days?')) return;
    setClearing(true);
    try {
      const res = await clearOldLogs(7);
      setClearMsg(`Deleted ${res.data.deleted} entries`);
      setTimeout(() => setClearMsg(''), 3000);
      fetchLogs(1, filters);
    } catch {
      setClearMsg('Clear failed.');
    } finally {
      setClearing(false);
    }
  };

  const formatTime = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });
  };

  const shortLogger = (name) => {
    if (!name) return '';
    const parts = name.split('.');
    return parts.length > 3 ? '…' + parts.slice(-2).join('.') : name;
  };

  return (
    <div>
      <h1 className="page-title">🪵 Application Logs</h1>

      {/* ── Filter bar ──────────────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>

          <FilterSelect label="Level" value={filters.level} options={LEVELS}
            onChange={v => applyFilter('level', v)} />

          <FilterSelect label="Tag" value={filters.tag} options={TAGS}
            onChange={v => applyFilter('tag', v)} />

          {/* Search */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: '1 1 200px', minWidth: 180 }}>
            <label style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Search message
            </label>
            <input
              className="input"
              placeholder="e.g. authenticate, kill switch…"
              value={filters.search}
              onChange={e => applyFilter('search', e.target.value)}
              style={{ fontSize: 12 }}
            />
          </div>

          {/* Date range */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <label style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              From
            </label>
            <input type="date" className="input" value={filters.date_from}
              onChange={e => applyFilter('date_from', e.target.value)}
              style={{ fontSize: 12, width: 130 }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <label style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              To
            </label>
            <input type="date" className="input" value={filters.date_to}
              onChange={e => applyFilter('date_to', e.target.value)}
              style={{ fontSize: 12, width: 130 }} />
          </div>

          {/* Controls */}
          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto', alignItems: 'flex-end' }}>
            <button
              onClick={() => setAutoRefresh(v => !v)}
              style={{
                padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)',
                background: autoRefresh ? 'rgba(34,197,94,0.15)' : 'var(--surface)',
                color: autoRefresh ? 'var(--green)' : 'var(--muted)',
                cursor: 'pointer', fontSize: 12, fontWeight: 600,
              }}
            >
              {autoRefresh ? '⏹ Auto' : '▶ Auto'}
            </button>
            <button
              onClick={() => fetchLogs(page, filters)}
              disabled={loading}
              className="btn btn-primary"
              style={{ padding: '6px 14px', fontSize: 12 }}
            >
              {loading ? '…' : '↻ Refresh'}
            </button>
            <button
              onClick={handleClear}
              disabled={clearing}
              style={{
                padding: '6px 12px', borderRadius: 6, border: '1px solid rgba(239,68,68,0.3)',
                background: 'rgba(239,68,68,0.08)', color: 'var(--red)',
                cursor: 'pointer', fontSize: 12,
              }}
            >
              {clearing ? '…' : '🗑 Clear >7d'}
            </button>
          </div>
        </div>

        {/* Summary row */}
        <div style={{ marginTop: 10, display: 'flex', gap: 16, alignItems: 'center', fontSize: 12 }}>
          <span style={{ color: 'var(--muted)' }}>
            {total.toLocaleString()} {total === 1 ? 'entry' : 'entries'}
            {(filters.level || filters.tag || filters.search) && ' (filtered)'}
          </span>
          {clearMsg && (
            <span style={{ color: 'var(--green)', fontWeight: 600 }}>{clearMsg}</span>
          )}
          {error && (
            <span style={{ color: 'var(--red)' }}>{error}</span>
          )}
        </div>
      </div>

      {/* ── Log table ───────────────────────────────────────────────────── */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {logs.length === 0 && !loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--muted)', fontSize: 14 }}>
            {error ? error : 'No log entries match the current filters.'}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'rgba(0,0,0,0.15)' }}>
                {['Time (IST)', 'Level', 'Tag', 'Logger', 'Message'].map((h, i) => (
                  <th key={h} style={{
                    padding: '10px 12px', textAlign: 'left', fontWeight: 700,
                    fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    width: i === 0 ? 155 : i === 1 ? 90 : i === 2 ? 90 : i === 3 ? 160 : 'auto',
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => {
                const isExpanded = expanded === log.id;
                const lvlStyle   = LEVEL_STYLE[log.level]  || {};
                return (
                  <React.Fragment key={log.id}>
                    <tr
                      onClick={() => setExpanded(isExpanded ? null : log.id)}
                      style={{
                        borderBottom: '1px solid var(--border)',
                        cursor: 'pointer',
                        background: isExpanded ? 'rgba(99,102,241,0.06)' : 'transparent',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
                      onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = 'transparent'; }}
                    >
                      {/* Time */}
                      <td style={{ padding: '9px 12px', color: 'var(--muted)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                        {formatTime(log.created_at)}
                      </td>
                      {/* Level */}
                      <td style={{ padding: '9px 12px' }}>
                        <Badge text={log.level} styleMap={LEVEL_STYLE} />
                      </td>
                      {/* Tag */}
                      <td style={{ padding: '9px 12px' }}>
                        {log.tag ? <Badge text={log.tag} styleMap={TAG_STYLE} /> : <span style={{ color: 'var(--muted)' }}>—</span>}
                      </td>
                      {/* Logger */}
                      <td style={{ padding: '9px 12px', color: 'var(--muted)', fontSize: 11, fontFamily: 'monospace' }}
                          title={log.logger_name}>
                        {shortLogger(log.logger_name)}
                      </td>
                      {/* Message (truncated) */}
                      <td style={{ padding: '9px 12px', color: lvlStyle.color || 'var(--text)', lineHeight: 1.5 }}>
                        <div style={{
                          overflow: isExpanded ? 'visible' : 'hidden',
                          textOverflow: isExpanded ? 'unset' : 'ellipsis',
                          whiteSpace: isExpanded ? 'pre-wrap' : 'nowrap',
                          maxWidth: isExpanded ? 'none' : 480,
                          wordBreak: isExpanded ? 'break-word' : 'normal',
                          fontFamily: isExpanded ? 'monospace' : 'inherit',
                          fontSize: isExpanded ? 11 : 12,
                        }}>
                          {log.message}
                        </div>
                        {isExpanded && (
                          <div style={{ marginTop: 6, fontSize: 10, color: 'var(--muted)' }}>
                            Logger: <code style={{ fontFamily: 'monospace' }}>{log.logger_name}</code>
                            {' · '}ID: {log.id}
                          </div>
                        )}
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>{/* end scroll wrapper */}
      </div>{/* end card */}

      {/* ── Pagination ──────────────────────────────────────────────────── */}
      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 16 }}>
          <button
            className="btn"
            disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
            style={{ padding: '5px 14px', fontSize: 12 }}
          >
            ← Prev
          </button>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>
            Page {page} of {pages}
          </span>
          <button
            className="btn"
            disabled={page >= pages}
            onClick={() => setPage(p => p + 1)}
            style={{ padding: '5px 14px', fontSize: 12 }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
